/**
 * Event Definition Editor Dialog
 *
 * Dialog for creating or editing notification event definitions.
 */

import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Checkbox } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { eventDefinitionsApiService } from "../../services/api/notifications";
import {
    EventDefinitionDto,
    CreateEventDefinitionRequest,
    UpdateEventDefinitionRequest,
    NotificationChannel,
    NotificationChannelLabels,
    NotificationPriority,
    getPriorityOptions,
    SmsMessageType,
    getSmsMessageTypeOptions,
    parseAllowedChannels,
    formatAllowedChannels,
    getDefaultAllowedChannels,
} from "../../models/notifications";

interface EventDefinitionEditorProps {
    merchantId: number;
    event: EventDefinitionDto | null; // null = create mode
    onSave: () => void;
    onCancel: () => void;
}

interface FormState {
    eventCode: string;
    description: string;
    allowedChannels: NotificationChannel[];
    allowedVariables: string[];
    allowedDataPayloadKeys: string[];
    priority: NotificationPriority;
    smsMessageType: SmsMessageType;
    isActive: boolean;
}

const ALL_CHANNELS: NotificationChannel[] = [
    NotificationChannel.Email,
    NotificationChannel.Sms,
    NotificationChannel.Push,
    NotificationChannel.Pull,
];

const EventDefinitionEditor: React.FC<EventDefinitionEditorProps> = ({
    merchantId,
    event,
    onSave,
    onCancel,
}) => {
    const toast = useRef<Toast>(null);
    const isEditMode = event !== null;

    const [form, setForm] = useState<FormState>({
        eventCode: "",
        description: "",
        allowedChannels: ALL_CHANNELS,
        allowedVariables: [],
        allowedDataPayloadKeys: [],
        priority: NotificationPriority.Normal,
        smsMessageType: SmsMessageType.Otp,
        isActive: true,
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (event) {
            setForm({
                eventCode: event.eventCode,
                description: event.description ?? "",
                allowedChannels: parseAllowedChannels(event.allowedChannels),
                allowedVariables: event.allowedVariables ?? [],
                allowedDataPayloadKeys: event.allowedDataPayloadKeys ?? [],
                priority: event.priority ?? NotificationPriority.Normal,
                smsMessageType: event.smsMessageType ?? SmsMessageType.Otp,
                isActive: event.isActive,
            });
        } else {
            setForm({
                eventCode: "",
                description: "",
                allowedChannels: ALL_CHANNELS,
                allowedVariables: [],
                allowedDataPayloadKeys: [],
                priority: NotificationPriority.Normal,
                smsMessageType: SmsMessageType.Otp,
                isActive: true,
            });
        }
    }, [event]);

    const updateForm = <K extends keyof FormState>(field: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        // Clear error when field changes
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    // Reset priority to Normal when Push is deselected
    useEffect(() => {
        if (!form.allowedChannels.includes(NotificationChannel.Push) && form.priority !== NotificationPriority.Normal) {
            updateForm("priority", NotificationPriority.Normal);
        }
    }, [form.allowedChannels]);

    // Reset SMS message type to Otp when Sms is deselected
    useEffect(() => {
        if (!form.allowedChannels.includes(NotificationChannel.Sms) && form.smsMessageType !== SmsMessageType.Otp) {
            updateForm("smsMessageType", SmsMessageType.Otp);
        }
    }, [form.allowedChannels]);

    const toggleChannel = (channel: NotificationChannel) => {
        const current = form.allowedChannels;
        const updated = current.includes(channel)
            ? current.filter((c) => c !== channel)
            : [...current, channel];
        updateForm("allowedChannels", updated);
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!form.eventCode.trim()) {
            newErrors.eventCode = "Event code is required";
        } else if (!/^[A-Z0-9_]+$/.test(form.eventCode)) {
            newErrors.eventCode = "Event code must contain only uppercase letters, numbers, and underscores";
        } else if (form.eventCode.length > 100) {
            newErrors.eventCode = "Event code must be 100 characters or less";
        }

        if (form.description && form.description.length > 500) {
            newErrors.description = "Description must be 500 characters or less";
        }

        if (form.allowedChannels.length === 0) {
            newErrors.allowedChannels = "At least one channel must be selected";
        }

        // Validate variable names
        const invalidVars = form.allowedVariables.filter((v) => !/^[a-zA-Z][a-zA-Z0-9_]*$/.test(v));
        if (invalidVars.length > 0) {
            newErrors.allowedVariables = `Invalid variable names: ${invalidVars.join(", ")}. Variables must start with a letter and contain only letters, numbers, and underscores.`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        setIsSaving(true);

        try {
            if (isEditMode) {
                const request: UpdateEventDefinitionRequest = {
                    description: form.description || undefined,
                    allowedChannels: formatAllowedChannels(form.allowedChannels),
                    allowedVariables: form.allowedVariables.length > 0 ? form.allowedVariables : undefined,
                    allowedDataPayloadKeys: form.allowedDataPayloadKeys.length > 0 ? form.allowedDataPayloadKeys : undefined,
                    priority: form.priority,
                    smsMessageType: form.smsMessageType,
                    isActive: form.isActive,
                };

                const result = await eventDefinitionsApiService.update(merchantId, event!.eventCode, request);
                if (result.success) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Updated",
                        detail: `Event "${form.eventCode}" has been updated`,
                        life: 3000,
                    });
                    setTimeout(onSave, 500);
                } else {
                    toast.current?.show({
                        severity: "error",
                        summary: "Error",
                        detail: result.message || "Failed to update event definition",
                        life: 4000,
                    });
                }
            } else {
                const request: CreateEventDefinitionRequest = {
                    merchantId,
                    eventCode: form.eventCode.toUpperCase(),
                    description: form.description || undefined,
                    allowedChannels: formatAllowedChannels(form.allowedChannels),
                    allowedVariables: form.allowedVariables.length > 0 ? form.allowedVariables : undefined,
                    allowedDataPayloadKeys: form.allowedDataPayloadKeys.length > 0 ? form.allowedDataPayloadKeys : undefined,
                    priority: form.priority,
                    smsMessageType: form.smsMessageType,
                    isActive: form.isActive,
                };

                const result = await eventDefinitionsApiService.create(request);
                if (result.success) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Created",
                        detail: `Event "${form.eventCode}" has been created`,
                        life: 3000,
                    });
                    setTimeout(onSave, 500);
                } else {
                    toast.current?.show({
                        severity: "error",
                        summary: "Error",
                        detail: result.message || "Failed to create event definition",
                        life: 4000,
                    });
                }
            }
        } finally {
            setIsSaving(false);
        }
    };

    const footer = (
        <div className="flex justify-end gap-2">
            <Button
                size="small"
                label="Cancel"
                icon="pi pi-times"
                outlined
                onClick={onCancel}
                disabled={isSaving}
            />
            <Button
                size="small"
                label={isEditMode ? "Update" : "Create"}
                icon="pi pi-check"
                onClick={handleSave}
                loading={isSaving}
            />
        </div>
    );

    return (
        <>
            <Toast ref={toast} position="bottom-center" />
            <Dialog
                header={isEditMode ? `Edit Event: ${event?.eventCode}` : "Create Event Definition"}
                visible
                style={{ width: "600px" }}
                modal
                onHide={onCancel}
                footer={footer}
                pt={{
                    content: { className: "overflow-y-auto max-h-[70vh]" },
                }}
            >
                <div className="space-y-4">
                    {/* Event Code */}
                    <div>
                        <label className="block text-sm font-semibold mb-1">
                            Event Code <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            value={form.eventCode}
                            onChange={(e) => updateForm("eventCode", e.target.value.toUpperCase())}
                            placeholder="e.g., PAYMENT_RECEIVED"
                            className={`w-full font-mono ${errors.eventCode ? "p-invalid" : ""}`}
                            disabled={isEditMode}
                            maxLength={100}
                        />
                        {errors.eventCode && (
                            <small className="p-error block mt-1">{errors.eventCode}</small>
                        )}
                        {!isEditMode && (
                            <small className="text-gray-500 block mt-1">
                                Use UPPER_SNAKE_CASE. This cannot be changed after creation.
                            </small>
                        )}
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-semibold mb-1">Description</label>
                        <InputTextarea
                            value={form.description}
                            onChange={(e) => updateForm("description", e.target.value)}
                            placeholder="Describe when this event is triggered..."
                            rows={3}
                            className={`w-full ${errors.description ? "p-invalid" : ""}`}
                            maxLength={500}
                        />
                        {errors.description && (
                            <small className="p-error block mt-1">{errors.description}</small>
                        )}
                        <small className="text-gray-500 block mt-1">
                            {form.description.length}/500 characters
                        </small>
                    </div>

                    {/* Allowed Channels */}
                    <div>
                        <label className="block text-sm font-semibold mb-2">
                            Allowed Channels <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-wrap gap-3">
                            {ALL_CHANNELS.map((channel) => (
                                <div
                                    key={channel}
                                    className="flex items-center gap-2"
                                >
                                    <Checkbox
                                        inputId={`channel-${channel}`}
                                        checked={form.allowedChannels.includes(channel)}
                                        onChange={() => toggleChannel(channel)}
                                    />
                                    <label
                                        htmlFor={`channel-${channel}`}
                                        className="cursor-pointer"
                                    >
                                        {NotificationChannelLabels[channel]}
                                    </label>
                                </div>
                            ))}
                        </div>
                        {errors.allowedChannels && (
                            <small className="p-error block mt-1">{errors.allowedChannels}</small>
                        )}
                    </div>

                    {/* Allowed Variables */}
                    <div>
                        <label className="block text-sm font-semibold mb-1">
                            Allowed Variables (Placeholders)
                        </label>
                        <Chips
                            value={form.allowedVariables}
                            onChange={(e) => updateForm("allowedVariables", e.value ?? [])}
                            placeholder="Type variable name and press Enter"
                            className={`w-full ${errors.allowedVariables ? "p-invalid" : ""}`}
                            separator=","
                        />
                        {errors.allowedVariables && (
                            <small className="p-error block mt-1">{errors.allowedVariables}</small>
                        )}
                        <small className="text-gray-500 block mt-1">
                            Define placeholders that can be used in templates (e.g., amount, currency, recipientName).
                            Press Enter or comma to add each variable.
                        </small>

                        {form.allowedVariables.length > 0 && (
                            <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                                <div className="text-sm font-medium text-gray-600 mb-2">Preview in templates:</div>
                                <div className="flex flex-wrap gap-1">
                                    {form.allowedVariables.map((v) => (
                                        <Tag
                                            key={v}
                                            value={`{{${v}}}`}
                                            className="font-mono text-xs bg-blue-100 text-blue-700"
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Allowed Data Payload Keys */}
                    {form.allowedChannels.includes(NotificationChannel.Push) && (
                        <div>
                            <label className="block text-sm font-semibold mb-1">
                                Allowed Data Payload Keys
                            </label>
                            <Chips
                                value={form.allowedDataPayloadKeys}
                                onChange={(e) => updateForm("allowedDataPayloadKeys", e.value ?? [])}
                                placeholder="Type key name and press Enter"
                                className="w-full"
                                separator=","
                            />
                            <small className="text-gray-500 block mt-1">
                                Keys that can be passed through to push notification data payloads.
                                Reserved keys (deliveryId, eventCode, merchantId, deepLink) are always included automatically.
                            </small>
                        </div>
                    )}

                    {/* Push Priority */}
                    {form.allowedChannels.includes(NotificationChannel.Push) && (
                        <div>
                            <label className="block text-sm font-semibold mb-1">
                                Push Priority
                            </label>
                            <Dropdown
                                value={form.priority}
                                options={getPriorityOptions()}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => updateForm("priority", e.value)}
                                className="w-full"
                            />
                            <small className="text-gray-500 block mt-1">
                                High priority notifications bypass battery optimization and are delivered immediately.
                                Use for time-sensitive events only (e.g., OTP codes, payment alerts).
                            </small>
                        </div>
                    )}

                    {/* SMS Message Type */}
                    {form.allowedChannels.includes(NotificationChannel.Sms) && (
                        <div>
                            <label className="block text-sm font-semibold mb-1">
                                SMS Message Type
                            </label>
                            <Dropdown
                                value={form.smsMessageType}
                                options={getSmsMessageTypeOptions()}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => updateForm("smsMessageType", e.value)}
                                className="w-full"
                            />
                            <small className="text-gray-500 block mt-1">
                                Determines how SMS providers classify the traffic. Use OTP for verification codes
                                and Transactional for alerts and notifications.
                            </small>
                        </div>
                    )}

                    {/* Is Active */}
                    <div className="flex items-center gap-2 pt-2">
                        <Checkbox
                            inputId="isActive"
                            checked={form.isActive}
                            onChange={(e) => updateForm("isActive", e.checked ?? true)}
                        />
                        <label htmlFor="isActive" className="cursor-pointer">
                            Active (can be used for new templates)
                        </label>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default EventDefinitionEditor;
